// File: frontend/src/components/common/InputClearButton.tsx
import React from "react";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./InputClearButton.css";

interface InputClearButtonProps {
  /** Clears the bound input's value (and any dependent state) */
  onClear: () => void;
  /**
   * "absolute" (default) floats the button over the right edge of the input —
   * the host element must be position: relative.
   * "inline" makes it a normal flex item, for search boxes that are already a
   * flex row (icon + input), where an overlay would sit on top of the caret.
   */
  variant?: "absolute" | "inline";
  /** Extra class for host-specific offsets */
  className?: string;
}

/**
 * The small "×" that empties a text input, matching the one in the message
 * search boxes (.new-message-clear-btn / .conversation-search-clear-btn).
 * Those two were hand-rolled per component; every input added since then was
 * missing the affordance, so it lives in one place from now on.
 *
 * Render it only when the input actually has a value — an always-visible ×
 * on an empty field reads as a broken control.
 */
export const InputClearButton: React.FC<InputClearButtonProps> = ({
  onClear,
  variant = "absolute",
  className = "",
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={`input-clear-btn input-clear-btn--${variant} ${className}`.trim()}
      // onMouseDown + preventDefault: several of these inputs close their
      // dropdown on blur, which would unmount the button before the click
      // landed. Handling mousedown clears the field before blur fires.
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClear();
      }}
      // The clear already happened on mousedown, but the click event still
      // bubbles afterwards. Several hosts (search bars, dropdown rows, table
      // rows) have their own click handlers on an ancestor, and letting the
      // click through fires them on a control the user never meant to hit.
      // Swallow it here so the button only ever does one thing.
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      title={t("input_clear", "Temizle")}
      aria-label={t("input_clear", "Temizle")}
    >
      <FaTimes />
    </button>
  );
};